<template>
  <div class="lead-messages">
    <app-spinner v-if="isLoadingThreads" />

    <template v-else-if="threadsError">
      <div class="lead-messages__state lead-messages__state--error">
        <p>{{ threadsError }}</p>
        <app-button size="small" @click.native="retryThreads">Retry</app-button>
      </div>
    </template>

    <template v-else-if="!threads.length">
      <div class="lead-messages__state">No threads found</div>
    </template>

    <template v-else>
      <div class="lead-messages__table">
        <div class="lead-messages__thead">
          <div>Thread</div>
          <div>Channel</div>
          <div>Status</div>
          <div>Messages</div>
          <div>Latest message</div>
        </div>

        <div
          v-for="thread in threads"
          :key="thread.id"
          class="lead-messages__accordion-item"
          :class="{ 'lead-messages__accordion-item--open': isThreadExpanded(thread.id) }"
        >
          <button
            type="button"
            class="lead-messages__row"
            @click="onSelectThread(thread.id)"
          >
            <div class="lead-messages__cell lead-messages__cell--main">
              <span class="lead-messages__chevron" :class="{ 'lead-messages__chevron--open': isThreadExpanded(thread.id) }"></span>
              <div class="lead-messages__thread-main">
                <div class="lead-messages__thread-contact">{{ thread.contactDisplay }}</div>
                <div class="lead-messages__thread-subject">{{ thread.subject || 'No subject' }}</div>
                <div class="lead-messages__thread-preview">{{ thread.latestPreview || '-' }}</div>
              </div>
            </div>

            <div class="lead-messages__cell">
              <div>{{ thread.channelTitle }}</div>
              <div class="lead-messages__sub">{{ thread.channelType }}</div>
            </div>

            <div class="lead-messages__cell">
              <span class="lead-messages__status">{{ thread.status || '-' }}</span>
            </div>

            <div class="lead-messages__cell">
              {{ thread.messagesCount }}
            </div>

            <div class="lead-messages__cell">
              <div>{{ formatDateTime(thread.latestMessageAt) }}</div>
              <div class="lead-messages__sub">{{ thread.viewMode }}</div>
            </div>
          </button>

          <transition name="slide-small">
            <div v-if="isThreadExpanded(thread.id)" class="lead-messages__panel">
              <div class="lead-messages__panel-head">
                <div>
                  <div class="lead-messages__panel-title">{{ thread.subject || 'No subject' }}</div>
                  <div class="lead-messages__panel-subtitle">{{ thread.channelTitle }} · {{ thread.contactDisplay }}</div>
                </div>
                <div class="lead-messages__panel-meta">
                  <span>{{ thread.messagesCount }} messages</span>
                  <span>{{ thread.viewMode }}</span>
                </div>
              </div>

              <app-spinner v-if="isThreadMessagesLoadingById(thread.id)" />

              <template v-else-if="getThreadMessagesError(thread.id)">
                <div class="lead-messages__state lead-messages__state--error">
                  <p>{{ getThreadMessagesError(thread.id) }}</p>
                  <app-button size="small" @click.native="retryThreadMessages(thread.id)">Retry</app-button>
                </div>
              </template>

              <template v-else-if="!getThreadMessagesList(thread.id).length">
                <div class="lead-messages__state">No messages found</div>
              </template>

              <div v-else class="lead-messages__messages">
                <template v-if="thread.viewMode === 'emailView'">
                  <div
                    v-for="message in getThreadMessagesList(thread.id)"
                    :key="message.id"
                    class="lead-messages__message lead-messages__message--email"
                  >
                    <button
                      type="button"
                      class="lead-messages__message-header"
                      @click="toggleMessage(thread.id, message.id)"
                    >
                      <div class="lead-messages__message-meta">
                        <span class="lead-messages__tag">{{ message.type }}</span>
                        <span>{{ formatDateTime(message.createdAt) }}</span>
                        <span v-if="message.deliveryStatus">Delivery: {{ message.deliveryStatus }}</span>
                        <span v-if="message.isAutoReply">Auto reply</span>
                      </div>
                      <div class="lead-messages__message-preview">{{ getMessagePreview(message) }}</div>
                      <span class="lead-messages__chevron" :class="{ 'lead-messages__chevron--open': isMessageExpanded(thread.id, message.id) }"></span>
                    </button>

                    <transition name="slide-small">
                      <div v-if="isMessageExpanded(thread.id, message.id)">
                        <div class="lead-messages__email-head">
                          <div><b>Subject:</b> {{ message.emailSubjectResolved }}</div>
                          <div><b>From:</b> {{ (message.emailMeta && message.emailMeta.from) || message.contact.email || '-' }}</div>
                          <div><b>To:</b> {{ (message.emailMeta && message.emailMeta.to) || '-' }}</div>
                          <div v-if="message.emailMeta && message.emailMeta.cc"><b>Cc:</b> {{ message.emailMeta.cc }}</div>
                          <div><b>Contact:</b> {{ message.contact.display }}</div>
                          <div v-if="message.agent"><b>Agent:</b> {{ message.agent.name }}</div>
                        </div>

                        <div class="lead-messages__message-body lead-messages__message-body--email">
                          <div
                            class="lead-messages__email-html"
                            v-html="getEmailBodyHtml(message)"
                          />
                        </div>
                      </div>
                    </transition>
                  </div>
                </template>

                <template v-else-if="thread.viewMode === 'whatsappView'">
                  <div class="lead-messages__wa-chat">
                    <div
                      v-for="message in getThreadMessagesList(thread.id)"
                      :key="message.id"
                      class="lead-messages__wa-row"
                      :class="{
                        'lead-messages__wa-row--inbound': message.type === 'INBOUND',
                        'lead-messages__wa-row--outbound': message.type === 'OUTBOUND'
                      }"
                    >
                      <div
                        class="lead-messages__wa-bubble"
                        :class="{
                          'lead-messages__wa-bubble--inbound': message.type === 'INBOUND',
                          'lead-messages__wa-bubble--outbound': message.type === 'OUTBOUND'
                        }"
                      >
                        <button
                          type="button"
                          class="lead-messages__message-header lead-messages__message-header--wa"
                          @click="toggleMessage(thread.id, message.id)"
                        >
                          <div class="lead-messages__wa-meta">
                            <span>{{ message.type }}</span>
                            <span>{{ formatDateTime(message.createdAt) }}</span>
                            <span v-if="message.agent">{{ message.agent.name }}</span>
                            <span v-else>{{ message.contact.display }}</span>
                          </div>
                          <div class="lead-messages__message-preview">{{ getMessagePreview(message) }}</div>
                          <span class="lead-messages__chevron" :class="{ 'lead-messages__chevron--open': isMessageExpanded(thread.id, message.id) }"></span>
                        </button>
                        <transition name="slide-small">
                          <div v-if="isMessageExpanded(thread.id, message.id)" class="lead-messages__wa-text">{{ message.messageText || '-' }}</div>
                        </transition>
                      </div>
                    </div>
                  </div>
                </template>

                <template v-else>
                  <div
                    v-for="message in getThreadMessagesList(thread.id)"
                    :key="message.id"
                    class="lead-messages__message lead-messages__message--generic"
                  >
                    <button
                      type="button"
                      class="lead-messages__message-header"
                      @click="toggleMessage(thread.id, message.id)"
                    >
                      <div class="lead-messages__message-meta">
                        <span class="lead-messages__tag">{{ message.type }}</span>
                        <span>{{ formatDateTime(message.createdAt) }}</span>
                        <span>{{ message.contact.display }}</span>
                        <span v-if="message.agent">{{ message.agent.name }}</span>
                      </div>
                      <div class="lead-messages__message-preview">{{ getMessagePreview(message) }}</div>
                      <span class="lead-messages__chevron" :class="{ 'lead-messages__chevron--open': isMessageExpanded(thread.id, message.id) }"></span>
                    </button>
                    <transition name="slide-small">
                      <div v-if="isMessageExpanded(thread.id, message.id)" class="lead-messages__message-body">{{ message.messageText || '-' }}</div>
                    </transition>
                  </div>
                </template>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { format, parse, parseISO } from 'date-fns'
import { getThreadMessages, getThreads } from './messages/messages.service'

export default {
  name: 'LeadsDetailMessages',
  props: {
    leadInfo: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      isLoadingThreads: false,
      threads: [],
      threadsError: '',
      expandedThreadId: null,
      messagesByThreadId: {},
      threadMessagesLoading: {},
      threadMessagesError: {},
      expandedMessageIdByThreadId: {},
    }
  },
  computed: {
    mockThreadsMode() {
      return this.$route.query.messagesThreadsMockMode || 'success'
    },
    mockMessagesMode() {
      return this.$route.query.messagesMockMode || 'success'
    },
  },
  created() {
    this.loadThreads()
  },
  methods: {
    loadThreads() {
      this.isLoadingThreads = true
      this.threadsError = ''
      this.threads = []
      this.expandedThreadId = null
      this.messagesByThreadId = {}
      this.threadMessagesLoading = {}
      this.threadMessagesError = {}
      this.expandedMessageIdByThreadId = {}

      getThreads({
        leadId: this.$route.params.id,
        mode: this.mockThreadsMode
      })
        .then(threads => {
          this.threads = threads
        })
        .catch(error => {
          this.threadsError = error?.message || 'Failed to load threads'
        })
        .finally(() => {
          this.isLoadingThreads = false
        })
    },
    retryThreads() {
      this.loadThreads()
    },
    isThreadExpanded(threadId) {
      return this.expandedThreadId === threadId
    },
    onSelectThread(threadId) {
      if (this.expandedThreadId === threadId) {
        this.expandedThreadId = null
        return
      }

      this.expandedThreadId = threadId
      this.ensureThreadMessagesLoaded(threadId)
    },
    ensureThreadMessagesLoaded(threadId) {
      if (Object.prototype.hasOwnProperty.call(this.messagesByThreadId, threadId)) return
      this.loadThreadMessages(threadId)
    },
    loadThreadMessages(threadId) {
      const thread = this.threads.find(item => item.id === threadId)

      this.$set(this.threadMessagesLoading, threadId, true)
      this.$set(this.threadMessagesError, threadId, '')

      getThreadMessages({
        threadId,
        thread,
        mode: this.mockMessagesMode
      })
        .then(messages => {
          this.$set(this.messagesByThreadId, threadId, messages)
        })
        .catch(error => {
          this.$set(this.threadMessagesError, threadId, error?.message || 'Failed to load messages')
          this.$set(this.messagesByThreadId, threadId, [])
        })
        .finally(() => {
          this.$set(this.threadMessagesLoading, threadId, false)
        })
    },
    retryThreadMessages(threadId) {
      this.loadThreadMessages(threadId)
    },
    getThreadMessagesList(threadId) {
      return this.messagesByThreadId[threadId] || []
    },
    isThreadMessagesLoadingById(threadId) {
      return !!this.threadMessagesLoading[threadId]
    },
    getThreadMessagesError(threadId) {
      return this.threadMessagesError[threadId] || ''
    },
    isMessageExpanded(threadId, messageId) {
      return this.expandedMessageIdByThreadId[threadId] === messageId
    },
    toggleMessage(threadId, messageId) {
      if (this.expandedMessageIdByThreadId[threadId] === messageId) {
        this.$set(this.expandedMessageIdByThreadId, threadId, null)
        return
      }

      this.$set(this.expandedMessageIdByThreadId, threadId, messageId)
    },
    getMessagePreview(message) {
      if (!message.messageText) return '-'

      const text = String(message.messageText).replace(/\s+/g, ' ').trim()
      if (!text) return '-'

      return text.length > 120 ? `${text.slice(0, 120)}...` : text
    },
    getEmailBodyHtml(message) {
      if (message.sanitizedHtml) return message.sanitizedHtml
      if (!message.messageText) return '-'

      return String(message.messageText).replace(/\r\n|\n|\r/g, '<br>')
    },
    formatDateTime(date) {
      if (!date) return '-'

      try {
        return format(parseISO(date), 'dd MMM yyyy, HH:mm')
      } catch (e) {
        try {
          return format(parse(date, 'yyyy-MM-dd HH:mm:ss', new Date()), 'dd MMM yyyy, HH:mm')
        } catch (err) {
          return '-'
        }
      }
    }
  }
}
</script>

<style scoped lang="sass">
.lead-messages__table
  border: 1px solid #ebebeb
  border-radius: 8px
  background: #fff
  overflow: hidden

.lead-messages__thead
  display: grid
  grid-template-columns: minmax(0, 2.2fr) 1.1fr 0.7fr 0.6fr 1fr
  gap: 12px
  padding: 12px 14px
  background: #f8fafb
  border-bottom: 1px solid #ebebeb
  font-size: 12px
  font-weight: 600
  color: #5f5f5f

  +max-w($laptop_md)
    display: none

.lead-messages__accordion-item
  border-bottom: 1px solid #f0f0f0

  &:last-child
    border-bottom: none

.lead-messages__accordion-item--open
  background: #fcfef9

.lead-messages__row
  width: 100%
  border: none
  background: transparent
  text-align: left
  display: grid
  grid-template-columns: minmax(0, 2.2fr) 1.1fr 0.7fr 0.6fr 1fr
  gap: 12px
  padding: 12px 14px
  align-items: start
  cursor: pointer

  &:hover
    background: #fafcfd

  +max-w($laptop_md)
    grid-template-columns: 1fr
    gap: 8px

.lead-messages__cell
  min-width: 0
  font-size: 13px
  color: #181818

.lead-messages__cell--main
  display: flex
  gap: 10px
  align-items: flex-start

.lead-messages__thread-main
  min-width: 0

.lead-messages__thread-contact
  font-weight: 600
  margin-bottom: 2px

.lead-messages__thread-subject
  margin-bottom: 3px
  color: #343434

.lead-messages__thread-preview
  color: #6f6f6f
  font-size: 12px
  line-height: 1.35
  white-space: nowrap
  overflow: hidden
  text-overflow: ellipsis

.lead-messages__sub
  margin-top: 3px
  font-size: 11px
  color: #7a7a7a

.lead-messages__status
  display: inline-flex
  align-items: center
  padding: 2px 8px
  border-radius: 999px
  background: #f1f4f6
  color: #455a64
  font-size: 11px
  text-transform: uppercase

.lead-messages__chevron
  width: 8px
  height: 8px
  margin-top: 5px
  border-right: 2px solid #667
  border-bottom: 2px solid #667
  transform: rotate(-45deg)
  transition: transform 0.2s ease
  flex-shrink: 0

.lead-messages__chevron--open
  transform: rotate(45deg)

.lead-messages__panel
  padding: 0 14px 14px
  border-top: 1px dashed #e6ecef

.lead-messages__panel-head
  display: flex
  justify-content: space-between
  gap: 12px
  padding: 12px 0

  +max-w($laptop_md)
    flex-direction: column

.lead-messages__panel-title
  font-weight: 600
  margin-bottom: 3px

.lead-messages__panel-subtitle
  font-size: 12px
  color: #6f6f6f

.lead-messages__panel-meta
  display: flex
  gap: 8px
  flex-wrap: wrap
  font-size: 12px
  color: #6f6f6f

.lead-messages__messages
  display: grid
  gap: 10px

.lead-messages__message
  border: 1px solid #f0f0f0
  border-radius: 8px
  padding: 10px 12px
  background: #fff

.lead-messages__message-header
  width: 100%
  border: none
  background: transparent
  padding: 0
  text-align: left
  display: grid
  grid-template-columns: 1fr auto
  gap: 6px 10px
  align-items: start
  cursor: pointer

.lead-messages__message-header--wa
  grid-template-columns: 1fr auto
  gap: 4px

.lead-messages__message-meta
  display: flex
  flex-wrap: wrap
  gap: 8px 12px
  margin-bottom: 8px
  font-size: 12px
  color: #6f6f6f

.lead-messages__message-header .lead-messages__message-meta
  margin-bottom: 0

.lead-messages__message-preview
  grid-column: 1
  font-size: 12px
  color: #5f5f5f
  white-space: nowrap
  overflow: hidden
  text-overflow: ellipsis

.lead-messages__message-header .lead-messages__chevron
  grid-column: 2
  grid-row: 1 / span 2
  margin-top: 6px

.lead-messages__message-header--wa .lead-messages__message-preview
  white-space: normal
  overflow: visible
  text-overflow: clip

.lead-messages__message-header--wa .lead-messages__chevron
  grid-column: 2
  grid-row: 1 / span 2
  margin-top: 6px

.lead-messages__message-body
  white-space: pre-wrap
  line-height: 1.5

.lead-messages__message--email
  border-color: #dfe7eb
  background: #fbfdff

.lead-messages__email-head
  display: grid
  gap: 4px
  margin-bottom: 10px
  font-size: 13px
  color: #3f3f3f

.lead-messages__message-body--email
  border-top: 1px solid #eef2f4
  padding-top: 10px
  margin-top: 10px

.lead-messages__email-html
  line-height: 1.5
  color: #181818
  overflow-wrap: anywhere

.lead-messages__email-text
  white-space: pre-wrap
  line-height: 1.5

.lead-messages__wa-chat
  display: grid
  gap: 8px

.lead-messages__wa-row
  display: flex

.lead-messages__wa-row--inbound
  justify-content: flex-start

.lead-messages__wa-row--outbound
  justify-content: flex-end

.lead-messages__wa-bubble
  display: inline-block
  max-width: 75%
  border-radius: 12px
  padding: 10px 12px
  border: 1px solid #e8e8e8

  +max-w($laptop_md)
    max-width: 100%

.lead-messages__wa-bubble--inbound
  background: #fff

.lead-messages__wa-bubble--outbound
  background: #e9f8e9
  border-color: #cfeacc

.lead-messages__wa-text
  white-space: pre-wrap
  line-height: 1.45
  margin-top: 6px

.lead-messages__wa-meta
  display: flex
  flex-wrap: wrap
  gap: 6px 10px
  font-size: 11px
  color: #6f6f6f

.lead-messages__tag
  display: inline-flex
  align-items: center
  padding: 1px 6px
  border-radius: 999px
  background: #f1f4f6
  color: #455a64

.lead-messages__state
  border: 1px dashed #d6d6d6
  border-radius: 8px
  padding: 20px
  background: #fff
  color: #5f5f5f

.lead-messages__state--error
  border-color: #e85c20
  color: #181818

  p
    margin: 0 0 10px

.slide-small-enter-active, .slide-small-leave-active
  transition: all 0.2s ease

.slide-small-enter, .slide-small-leave-to
  opacity: 0
</style>
